import React, { Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  ImageProvider,
  VideoProvider,
  AudioProvider,
  DEFAULT_IMAGE_PROVIDER,
  DEFAULT_IMAGE_MODEL,
  DEFAULT_VIDEO_PROVIDER,
  DEFAULT_VIDEO_MODEL,
  DEFAULT_AUDIO_PROVIDER,
  DEFAULT_AUDIO_MODEL,
} from './api/structs/providers';
import { MediaOutputSpec, MediaProfile } from './api/structs/media-spec';
import { settingsToOutputSpec } from './components/selectors/modelSettingsHelpers';
import { Header, Toast } from './components/layout';
import { AddUserModal } from './components/modals';
import { useWebSocketEvents } from './hooks';
import API from './api/api';
import { rehydrateTelegramSendJobs } from './components/clips/telegramSendState';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useUserAccountState } from './hooks/useUserAccountState';
import { ToastProviderWithState } from './hooks/useToast';
import { applyTheme, applyRounded, getDocumentTheme, getDocumentRounded, isThemeMode, isRoundedMode, RoundedMode, THEME_STORAGE_KEY, ROUNDED_STORAGE_KEY } from './theme';
import { ClipEditorProvider } from './context/ClipEditorContext';
import { ToastMessage } from './toast';
import { AuthProvider, useAuth } from './context/AuthContext'; // VERIFY
import { AccessProvider, useAccess } from './context/AccessContext';
import { FeatureKey } from './api/access';
import LoginPage from './components/auth/LoginPage'; // VERIFY
import ErrorBoundary from './components/ErrorBoundary';
import { CreditsProvider } from './hooks/useCredits';
import { setOnCreditsError } from './api/auth';

const loadStudioShell = () => import('./components/studio-shell/StudioShell');
const loadGlobalFloatingEditor = () => import('./components/clips/GlobalFloatingEditor').then((module) => ({ default: module.GlobalFloatingEditor }));
const loadClipsBrowser = () => import('./components/clips/ClipsBrowser');
const loadPipelineView = () => import('./components/pipeline/PipelineManager');
const loadDocsView = () => import('./components/docs/DocsView');
const loadBuilderView = () => import('./components/builder/BuilderTabs');
const loadChatView = () => import('./components/chat/ChatView');
const loadPlaybooksView = () => import('./components/playbooks/PlaybooksPage');
const loadGenerateView = () => import('./components/generate/GenerateView');
const loadGeneratorsView = () => import('./components/generators/GeneratorsView');
const loadSeriesView = () => import('./components/series/SeriesView');
const loadDatasetsView = () => import('./components/datasets/DatasetsView');
const loadTrainingView = () => import('./components/training/TrainingView');
const loadAdminConsole = () => import('./components/admin/AdminConsole');
const loadArchitecturePage = () => import('./pages/ArchitecturePage');
const loadMediaLibraryPage = () => import('./pages/MediaLibraryPage');

const StudioShell = React.lazy(loadStudioShell);
const GlobalFloatingEditor = React.lazy(loadGlobalFloatingEditor);
const ClipsBrowser = React.lazy(loadClipsBrowser);
const PipelineView = React.lazy(loadPipelineView);
const DocsView = React.lazy(loadDocsView);
const BuilderTabs = React.lazy(loadBuilderView);
const ChatView = React.lazy(loadChatView);
const PlaybooksPage = React.lazy(loadPlaybooksView);
const GenerateView = React.lazy(loadGenerateView);
const GeneratorsView = React.lazy(loadGeneratorsView);
const SeriesView = React.lazy(loadSeriesView);
const DatasetsView = React.lazy(loadDatasetsView);
const TrainingView = React.lazy(loadTrainingView);
const AdminConsole = React.lazy(loadAdminConsole);
const ArchitecturePage = React.lazy(loadArchitecturePage);
const MediaLibraryPage = React.lazy(loadMediaLibraryPage);

const routeLoaders = [
  loadStudioShell,
  loadClipsBrowser,
  loadPipelineView,
  loadDocsView,
  loadBuilderView,
  loadChatView,
  loadPlaybooksView,
  loadGenerateView,
  loadGeneratorsView,
  loadSeriesView,
  loadDatasetsView,
  loadTrainingView,
  loadAdminConsole,
  loadArchitecturePage,
  loadMediaLibraryPage,
  loadGlobalFloatingEditor,
];

const RouteLoadingFallback: React.FC = () => (
  <div className="page-container h-full py-6" role="status" aria-live="polite" aria-label="Loading page">
    <div className="card h-full min-h-[18rem] overflow-hidden">
      <div className="card-body space-y-5">
        <div className="h-6 w-44 max-w-[50%] animate-pulse bg-[var(--border-subtle)]" />
        <div className="grid gap-4 md:grid-cols-3">
          <div className="h-28 animate-pulse bg-[var(--surface-2)]" />
          <div className="h-28 animate-pulse bg-[var(--surface-2)] [animation-delay:100ms]" />
          <div className="h-28 animate-pulse bg-[var(--surface-2)] [animation-delay:200ms]" />
        </div>
        <span className="sr-only">Loading page…</span>
      </div>
    </div>
  </div>
);

const PanelLoadingFallback: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex h-full min-h-48 flex-col gap-4" role="status" aria-label={`Loading ${label}`}>
    <div className="h-5 w-32 animate-pulse bg-[var(--border-subtle)]" />
    <div className="h-20 animate-pulse bg-[var(--surface-2)]" />
    <div className="h-20 animate-pulse bg-[var(--surface-2)] [animation-delay:120ms]" />
    <span className="sr-only">Loading {label}…</span>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ClipEditorProvider>
            <AuthGate />
          </ClipEditorProvider>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function AuthGate() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <LoginPage />;
  return (
    <CreditsProvider>
      <AccessProvider>
        <AppContent />
      </AccessProvider>
    </CreditsProvider>
  );
}

function AdminRoute() {
  const { isAdmin, loaded } = useAccess();
  if (!loaded) return <RouteLoadingFallback />;
  if (!isAdmin) {
    return (
      <div className="page-container h-full py-6 overflow-y-auto">
        <div className="card">
          <div className="card-body">
            <h2 className="text-lg font-semibold text-white">Forbidden</h2>
            <p className="text-sm text-slate-400 mt-2">You don't have access to the admin panel.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="page-container h-full py-6 overflow-y-auto">
      <AdminConsole />
    </div>
  );
}

function FeatureRoute({ feature, children }: { feature: FeatureKey; children: React.ReactNode }) {
  const { loaded, hasFeature } = useAccess();
  if (!loaded) return <RouteLoadingFallback />;
  if (!hasFeature(feature)) return feature === 'studio' ? <NoAccessView /> : <Navigate to="/studio" replace />;
  return <>{children}</>;
}

function NoAccessView() {
  return (
    <div className="page-container h-full py-6 overflow-y-auto">
      <div className="card max-w-2xl">
        <div className="card-body">
          <h2 className="text-xl font-semibold text-white">No Workspace Access</h2>
          <p className="text-sm text-slate-400 mt-2">Ask an admin to enable a workspace tab for this account.</p>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const [clipsRefreshTrigger, setClipsRefreshTrigger] = useState(0);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [storedThemeMode, setStoredThemeMode] = useLocalStorage<string>(THEME_STORAGE_KEY, getDocumentTheme());
  const [storedRoundedMode, setStoredRoundedMode] = useLocalStorage<string>(ROUNDED_STORAGE_KEY, getDocumentRounded());
  const themeMode = isThemeMode(storedThemeMode) ? storedThemeMode : 'dark';
  const roundedMode: RoundedMode = isRoundedMode(storedRoundedMode) ? storedRoundedMode : 'sharp';

  // Image provider state
  const [imageProvider] = useLocalStorage<ImageProvider>('imageProvider', DEFAULT_IMAGE_PROVIDER);
  const [imageModel] = useLocalStorage('imageModel', DEFAULT_IMAGE_MODEL);

  // Video provider state
  const [videoProvider] = useLocalStorage<VideoProvider>('videoProvider', DEFAULT_VIDEO_PROVIDER);
  const [videoModel] = useLocalStorage('videoModel', DEFAULT_VIDEO_MODEL);

  // Audio provider state
  const [audioProvider] = useLocalStorage<AudioProvider>('audioProvider', DEFAULT_AUDIO_PROVIDER);
  const [audioModel] = useLocalStorage('audioModel', DEFAULT_AUDIO_MODEL);

  // Per-modality settings (provider-specific extras like width, height, steps, etc.)
  const [imageSettings] = useLocalStorage<Partial<MediaOutputSpec>>('imageSettings', {});
  const [videoSettings] = useLocalStorage<Partial<MediaOutputSpec>>('videoSettings', {});
  const [audioSettings] = useLocalStorage<Partial<MediaOutputSpec>>('audioSettings', {});

  // Modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const pushToast = useCallback((message: ToastMessage) => setToast(message), []);
  const {
    users,
    activeUser,
    activeAccount,
    handleSelectUser,
    handleRemoveUser,
    handleSelectAccount,
    handleAddUser,
  } = useUserAccountState(pushToast);

  const mediaProfile = useMemo<MediaProfile>(() => {
    const profile: MediaProfile = {};

    if (imageProvider && imageModel) {
      profile.image = { provider: imageProvider, model: imageModel, ...settingsToOutputSpec(imageSettings) };
    }
    if (videoProvider && videoModel) {
      profile.video = { provider: videoProvider, model: videoModel, ...settingsToOutputSpec(videoSettings) };
    }
    if (audioProvider && audioModel) {
      profile.audio = { provider: audioProvider, model: audioModel, ...settingsToOutputSpec(audioSettings) };
    }

    return profile;
  }, [imageProvider, imageModel, imageSettings, videoProvider, videoModel, videoSettings, audioProvider, audioModel, audioSettings]);

  // WebSocket for real-time updates. Studio runs are polled by the shell's
  // usePipelineRuns, so the legacy ideas refresh signal has no consumer left.
  const refreshIdeas = useCallback(() => undefined, []);
  const refreshClips = useCallback(() => setClipsRefreshTrigger((r) => r + 1), []);
  
  useWebSocketEvents({ 
    onRefreshIdeas: refreshIdeas,
    onRefreshClips: refreshClips,
    onToast: (message) =>
      setToast(typeof message === 'string' ? { text: message, level: 'info' } : message),
  });

  // A backgrounded Telegram send outlives the page that started it, so on load
  // we ask the backend how any still-running job ended rather than leaving its
  // button spinning forever.
  useEffect(() => {
    void rehydrateTelegramSendJobs((jobId) => API.getTelegramSendJob(jobId));
  }, []);

  useEffect(() => {
    applyTheme(themeMode);
    applyRounded(roundedMode);
  }, [themeMode, roundedMode]);

  useEffect(() => {
    setOnCreditsError((e) => {
      const text =
        e.code === 'insufficient_credits'
          ? `Not enough credits${e.need ? ` (need ${e.need.toLocaleString()})` : ''} — ask admin to top up.`
          : e.code === 'daily_limit_exceeded'
          ? 'Daily credit limit reached — try again tomorrow or ask admin to raise your cap.'
          : e.code === 'balance_exhausted'
          ? 'Credits exhausted mid-run — pipeline halted. Ask admin to top up.'
          : e.code === 'pricing_not_configured'
          ? 'Pricing unavailable for this model — pick a different provider.'
          : `Credits error: ${e.message || e.code}`;
      setToast({ text, level: 'warning' });
    });
  }, []);

  // Keep the first authenticated paint small, then warm route chunks one at a
  // time after the shell is interactive. A later tab click is usually instant,
  // without flooding the initial request queue with every feature bundle.
  useEffect(() => {
    let loaderIndex = 0;
    let timer = window.setTimeout(function loadNextRoute() {
      const loader = routeLoaders[loaderIndex];
      loaderIndex += 1;
      if (loader) void loader().catch(() => undefined);
      if (loaderIndex < routeLoaders.length) {
        timer = window.setTimeout(loadNextRoute, 300);
      }
    }, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const { loaded: accessLoaded, hasFeature } = useAccess();

  // Thread-centric studio: narrow thread rail + one full-width stage.
  const studioView = (
    <div className="page-container h-full py-6 flex flex-col">
      <Suspense fallback={<PanelLoadingFallback label="studio" />}>
        <StudioShell
          clipsRefreshTrigger={clipsRefreshTrigger}
          onClipsCreated={refreshClips}
          mediaProfile={mediaProfile}
          activeAccount={activeAccount}
          activeUser={activeUser}
        />
      </Suspense>
    </div>
  );

  // Same card frame as the Builder: the chat fills it and scrolls inside.
  const chatView = (
    <FeatureRoute feature="chat">
      <div className="page-container h-full py-6 overflow-hidden">
        <div className="card h-full flex flex-col min-h-0 overflow-hidden">
          <ChatView />
        </div>
      </div>
    </FeatureRoute>
  );

  // The operator's playbooks: the chat's feature, linked from the chat's sidebar.
  const playbooksView = (
    <FeatureRoute feature="chat">
      <div className="page-container h-full py-6 overflow-hidden">
        <div className="card h-full flex flex-col min-h-0 overflow-hidden">
          <PlaybooksPage />
        </div>
      </div>
    </FeatureRoute>
  );

  return (
    <ToastProviderWithState toast={toast} setToast={setToast}>
    <div className="app-shell h-screen flex flex-col">
      <Toast message={toast} onClose={() => setToast(null)} />

      <Header
        users={users}
        activeUser={activeUser}
        activeAccount={activeAccount}
        onAddUser={() => setShowAddUserModal(true)}
        onSelectUser={handleSelectUser}
        onRemoveUser={handleRemoveUser}
        onSelectAccount={handleSelectAccount}
        themeMode={themeMode}
        roundedMode={roundedMode}
        onThemeToggle={() => setStoredThemeMode(themeMode === 'dark' ? 'light' : 'dark')}
        onRoundedToggle={() => setStoredRoundedMode(roundedMode === 'rounded' ? 'sharp' : 'rounded')}
      />

      <main className="flex-1 overflow-hidden">
        <Suspense fallback={<RouteLoadingFallback />}>
          <BuilderMount />
          <Routes>
          <Route path="/" element={<Navigate to="/studio" replace />} />
          <Route path="/studio" element={<FeatureRoute feature="studio">{studioView}</FeatureRoute>} />
          <Route path="/chat" element={chatView} />
          <Route path="/chat/:threadId" element={chatView} />
          <Route path="/playbooks" element={playbooksView} />
          <Route path="/playbooks/:slug" element={playbooksView} />
          <Route path="/generate" element={<FeatureRoute feature="generate"><GenerateView /></FeatureRoute>} />
          <Route
            path="/series/*"
            element={
              <FeatureRoute feature="series">
                <div className="page-container h-full py-6 overflow-y-auto">
                  <SeriesView />
                </div>
              </FeatureRoute>
            }
          />
          <Route
            path="/archive"
            element={
              <FeatureRoute feature="archive">
                <div className="page-container h-full py-6 overflow-hidden">
                  <div className="card h-full flex flex-col min-h-0">
                    <div className="card-body flex-1 flex flex-col min-h-0">
                      <ClipsBrowser onClipLoaded={refreshClips} />
                    </div>
                  </div>
                </div>
              </FeatureRoute>
            }
          />
          <Route path="/pipeline" element={<FeatureRoute feature="pipeline_manager"><PipelineView /></FeatureRoute>} />
          <Route
            path="/generators"
            element={
              <FeatureRoute feature="generators">
                {/* Unlike the other routes' shared `page-container` (max-w-7xl mx-auto),
                    the generators view hosts the ProfileBuilder canvas, which the user
                    wants to dynamically fill the full viewport width rather than being
                    boxed into a centered column — so this route keeps only the matching
                    horizontal gutter, not the max-width/centering. */}
                <div className="h-full w-full px-4 py-6 overflow-hidden">
                  <div className="card h-full flex flex-col min-h-0 overflow-hidden">
                    <div className="card-body flex-1 flex flex-col min-h-0">
                      <GeneratorsView />
                    </div>
                  </div>
                </div>
              </FeatureRoute>
            }
          />
          <Route
            path="/datasets"
            element={<FeatureRoute feature="datasets"><DatasetsView /></FeatureRoute>}
          />
          <Route
            path="/training"
            element={<FeatureRoute feature="training"><TrainingView /></FeatureRoute>}
          />
          <Route
            path="/docs"
            element={
              <FeatureRoute feature="docs">
                <div className="page-container h-full py-6 overflow-y-auto">
                  <DocsView />
                </div>
              </FeatureRoute>
            }
          />
          <Route
            path="/media"
            element={
              <FeatureRoute feature="upload_media">
                <MediaLibraryPage />
              </FeatureRoute>
            }
          />
          <Route path="/architecture/*" element={<ArchitecturePage />} />
          <Route path="/builder/*" element={!accessLoaded || hasFeature('builder') ? null : <Navigate to="/studio" replace />} />
          <Route path="/admin" element={<AdminRoute />} />
          <Route path="*" element={<Navigate to="/studio" replace />} />
          </Routes>
        </Suspense>
      </main>

      <Suspense fallback={null}>
        <GlobalFloatingEditor />
      </Suspense>

      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSubmit={handleAddUser}
      />
    </div>
    </ToastProviderWithState>
  );
}

function BuilderMount() {
  const { pathname } = useLocation();
  const { hasFeature } = useAccess();
  const builderActive = pathname === '/builder' || pathname.startsWith('/builder/');
  if (!hasFeature('builder') || !builderActive) return null;
  return (
    <div className="page-container h-full py-6 overflow-hidden">
      <div className="card h-full flex flex-col min-h-0 overflow-hidden">
        <BuilderTabs />
      </div>
    </div>
  );
}

export default App;
